import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReminderModal from './_reminderModal';

export default function PropertiesTable({ properties, isLoading, subscriptionStatus, subscription }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const itemsPerPage = 5;
    const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
    const [propertySelected, setSelectedPropetySelected] = useState(null)

    const handleReminderModalOpen = (id) => {
        setIsReminderModalOpen(true);
        setSelectedPropetySelected(id)
    };

    // Ensure properties and related objects are always defined
    const safeProperties = Array.isArray(properties) ? properties : [];
    const safeSubscriptionStatus = subscriptionStatus || {};
    const safeSubscription = subscription || {};

    // Filter properties based on search term
    const filteredProperties = safeProperties.filter(property =>
        property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        property.location.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort properties
    const sortedProperties = [...filteredProperties].sort((a, b) => {
        if (!sortConfig.key) return 0;

        if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
    });

    // Pagination
    const totalPages = Math.ceil(sortedProperties.length / itemsPerPage);
    const displayedItems = sortedProperties.slice(
        currentPage * itemsPerPage,
        (currentPage + 1) * itemsPerPage
    );

    const handlePrevPage = () => {
        setCurrentPage(prev => Math.max(prev - 1, 0));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => Math.min(prev + 1, totalPages - 1));
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
        setCurrentPage(0); // Reset to first page when sorting changes
    };

    const getPercentageBadgeClass = (percentage) => {
        if (percentage >= 67) return "bg-green-600 text-white";
        if (percentage >= 33) return "bg-warning text-white";
        return "bg-red-600 text-white";
    };
    // Reset to first page when search term changes
    useEffect(() => {
        setCurrentPage(0);
    }, [searchTerm]);

    return (
        <div className="bg-white shadow-lg p-4 rounded-lg w-full">
            <div className="flex justify-between items-center mb-4">
                <h5 className="font-semibold text-lg">My List of Properties</h5>
                <div className="relative">
                    <input
                        type="text"
                        placeholder="Search properties..."
                        className="py-2 pr-4 pl-8 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <svg
                        className="top-2.5 left-2.5 absolute w-4 h-4 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="border-b">
                        <tr>
                            <th className="py-2 text-left">Image</th>
                            <th
                                className="hover:bg-gray-100 py-2 text-left cursor-pointer"
                                onClick={() => handleSort('name')}
                            >
                                Property {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                            </th>
                            <th
                                className="hover:bg-gray-100 py-2 text-left cursor-pointer"
                                onClick={() => handleSort('num_houses')}
                            >
                                Units {sortConfig.key === 'num_houses' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                            </th>
                            <th
                                className="hover:bg-gray-100 py-2 text-left cursor-pointer"
                                onClick={() => handleSort('percentage_paid')}
                            >
                                Percentage Paid {sortConfig.key === 'percentage_paid' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                            </th>
                            <th
                                className="hover:bg-gray-100 py-2 text-left cursor-pointer"
                                onClick={() => handleSort('expected_rent')}
                            >
                                Expected Rent {sortConfig.key === 'expected_rent' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                            </th>
                            <th
                                className="hover:bg-gray-100 py-2 text-left cursor-pointer"
                                onClick={() => handleSort('paid_rent')}
                            >
                                Paid Rent {sortConfig.key === 'paid_rent' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                            </th>
                            <th
                                className="hover:bg-gray-100 py-2 text-left cursor-pointer"
                                onClick={() => handleSort('defaulters')}
                            >
                                Defaulters {sortConfig.key === 'defaulters' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                            </th>
                            <th className="py-2 text-left">Reminders</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan="8" className="py-4 text-center">Loading...</td>
                            </tr>
                        ) : displayedItems.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="py-4 text-center">
                                    {searchTerm ? 'No properties match your search.' : 'No properties found.'}
                                </td>
                            </tr>
                        ) : (
                            displayedItems.map((property) => (
                                property.is_visible && (
                                    <tr key={property.id} className="border-b">
                                        <td className="py-1">
                                            <img
                                                src={property.property_image}
                                                alt={property.name}
                                                className="rounded-md w-16 h-12 object-cover"
                                            />
                                        </td>
                                        <td className="py-1">
                                            {/* <Link
                                                to={
                                                    property.num_houses !== 0
                                                        ? safeSubscriptionStatus[property.id] === "No subscription found"
                                                            ? `/new-plan/${property.id}`
                                                            : safeSubscription[property.id]?.[0]?.status !== "paid"
                                                                ? `/wallet-data/${property.id}`
                                                                : `/selected-property/${property.id}`
                                                        : `/selected-property/${property.id}`
                                                }
                                                className="text-blue-600 hover:underline"
                                            >
                                                {property.name}
                                            </Link> */}
                                            <Link to={`/selected-property/${property.id}`} className="text-blue-600 hover:underline"   >
                                                {property.name}
                                            </Link>
                                        </td>
                                        <td className="py-1">{property.num_houses}</td>
                                        <td className="py-1">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPercentageBadgeClass(property.percentage_paid)}`}>
                                                {property.percentage_paid}%
                                            </span>
                                        </td>
                                        <td className="py-1">Ksh. {property.expected_rent?.toLocaleString()}</td>
                                        <td className="py-1">Ksh. {property.paid_rent?.toLocaleString()}</td>
                                        <td className="py-1">{property.defaulters || 0}</td>
                                        <td className="py-1">
                                            {property.defaulters > 0 && (
                                                <button
                                                    onClick={() => handleReminderModalOpen(property.id)}
                                                    className="bg-red-600 hover:bg-red-700 px-2 py-2 rounded text-white text-xs"
                                                >
                                                    Send Reminders
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                )
                            ))
                        )}
                    </tbody>
                </table>
            </div>


            {/* the reminder modal */}
            <ReminderModal
              isOpen={isReminderModalOpen}
              onClose={() => setIsReminderModalOpen(false)}
              reminderType='apartment'
              apartment={propertySelected}
            />

            <div className="flex justify-between items-center mt-4">
                <div className="text-gray-600 text-sm">
                    Showing {displayedItems.length} of {filteredProperties.length} properties
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={handlePrevPage}
                        disabled={currentPage === 0}
                        className={`px-3 py-1 rounded ${currentPage === 0 ? 'bg-gray-200 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                    >
                        Previous
                    </button>
                    <span className="text-sm">
                        Page {currentPage + 1} of {totalPages || 1}
                    </span>
                    <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages - 1 || totalPages === 0}
                        className={`px-3 py-1 rounded ${currentPage === totalPages - 1 || totalPages === 0 ? 'bg-gray-200 cursor-not-allowed' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
}